import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  first,
  firstValueFrom,
  Observable,
  ReplaySubject,
} from 'rxjs';
import { User } from '../users/user.model';
import { MicrosoftAuthService } from './microsoft-auth.service';

interface TokenResponse {
  access_token: string;
  expires_in: number;
  refresh_token?: string;
}

// Flag in localStorage marking browsers that still hold a refresh cookie.
const HAS_REFRESH_SESSION_KEY = 'pt.hasRefreshSession';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _user$: ReplaySubject<User | undefined> = new ReplaySubject<
    User | undefined
  >(1);
  private _loggedIn$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private _error$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  private refreshTimeoutHandle: number | null = null;
  private refreshInFlight?: Promise<boolean>;
  private readonly maxRefreshDelayMs = 2_147_483_647; // clamp to Int32 max to avoid immediate timeouts
  private accessToken: string | null = null;
  private accessTokenExpiresAt: number | null = null;
  private microsoftSession = false;
  private currentUser?: User;
  private userResolved = false;
  private userLoadInFlight?: Promise<boolean>;

  constructor(
    private http: HttpClient,
    private microsoftAuthService: MicrosoftAuthService
  ) {
    this._loggedIn$.next(false);
    this._user$.next(undefined);
    this.bootstrapFromRefreshCookie();
  }

  // Attempts to hydrate the current user using the refresh cookie.
  async updateStatus(): Promise<boolean> {
    const hasToken = await this.ensureAccessToken();

    if (!hasToken) {
      this.setUser(undefined, false);
      return false;
    }

    try {
      const user = await firstValueFrom(this.http.get<User>('/api/auth'));
      this.setUser(user);
      this._error$.next(false);
      this.microsoftSession = !!user?.oid;
      return true;
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.handleAuthFailure(error);
      } else {
        this.userResolved = true;
      }
      return false;
    }
  }

  login(email: string, password: string): void {
    this.http
      .post<TokenResponse>('/api/auth', {
        email: email,
        password: password,
      })
      .pipe(first())
      .subscribe({
        next: (res) => {
          this.persistTokens(res);
          this._loggedIn$.next(true);
          this._error$.next(false);
          this.microsoftSession = false;
          void this.updateStatus();
        },
        error: (err: HttpErrorResponse) => {
          this.handleAuthFailure(err);
        },
      });
  }

  // Exchanges Microsoft access tokens for backend-issued credentials.
  async loginWithMicrosoft(accessToken: string): Promise<boolean> {
    try {
      const res = await firstValueFrom(
        this.http.post<TokenResponse>(
          '/api/auth/microsoft',
          {},
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        )
      );
      this.persistTokens(res);
      this._loggedIn$.next(true);
      this._error$.next(false);
      this.microsoftSession = true;
      void this.updateStatus();
      return true;
    } catch (err) {
      if (err instanceof HttpErrorResponse) {
        this.handleAuthFailure(err);
        return false;
      }
      this.handleAuthFailure();
      return false;
    }
  }

  // Clears frontend token state and notifies the backend.
  async logout(): Promise<void> {
    this.clearTokens();
    this.setHasRefreshSession(false);
    this.setUser(undefined, false);
    this._error$.next(false);

    try {
      await firstValueFrom(this.http.post('/api/auth/logout', {}));
    } catch {
      // swallow backend logout errors
    }

    this.microsoftSession = false;
  }

  public get error(): Observable<boolean> {
    return this._error$.asObservable();
  }

  public get user(): Observable<User | undefined> {
    return this._user$.asObservable();
  }

  public get loggedIn(): Observable<boolean> {
    return this._loggedIn$.asObservable();
  }

  public hasValidAccessToken(): boolean {
    return !!this.accessToken && !this.isAccessTokenExpired();
  }

  public getAccessToken(): string | null {
    return this.accessToken;
  }

  // Ensures an access token is present, silently refreshing if possible.
  public async awaitAccessToken(): Promise<string | null> {
    const hasToken = await this.ensureAccessToken();
    return hasToken ? this.accessToken : null;
  }

  public hasResolvedUser(): boolean {
    return this.userResolved;
  }

  public getCurrentUserSnapshot(): User | undefined {
    return this.currentUser;
  }

  public async ensureUserLoaded(): Promise<boolean> {
    if (this.userResolved && this.currentUser) {
      return true;
    }

    if (this.userLoadInFlight) {
      return this.userLoadInFlight;
    }

    this.userLoadInFlight = this.updateStatus().finally(() => {
      this.userLoadInFlight = undefined;
    });

    return this.userLoadInFlight;
  }

  userCount(): Observable<{ userCount: number }> {
    return this.http.get<{ userCount: number }>('/api/auth/user-count');
  }

  // Tries to revive a login silently when the backend refresh cookie exists.
  private bootstrapFromRefreshCookie(): void {
    if (!this.hasRefreshSession()) {
      return;
    }

    void this.refreshAccessToken(false).catch(() => undefined);
  }

  // Stores newly issued tokens and schedules the next refresh.
  private persistTokens(response: TokenResponse): void {
    this.accessToken = response.access_token;
    this.setHasRefreshSession(true);
    this.userResolved = false;
    this.currentUser = undefined;
    const expiresAt = this.resolveAccessTokenExpiry(response);
    if (expiresAt === undefined) {
      this.clearTokens();
      return;
    }

    this.accessTokenExpiresAt = expiresAt;
    this.scheduleRefresh(expiresAt);
  }

  // Plans a refresh shortly before the access token expires.
  private scheduleRefresh(expiresAt: number): void {
    if (this.refreshTimeoutHandle !== null) {
      window.clearTimeout(this.refreshTimeoutHandle);
    }

    if (!Number.isFinite(expiresAt)) {
      return;
    }

    const refreshIn = Math.max(expiresAt - Date.now() - 60_000, 0);
    const delay = Math.min(refreshIn, this.maxRefreshDelayMs);
    this.refreshTimeoutHandle = window.setTimeout(() => {
      void this.refreshAccessToken();
    }, delay);
  }

  // Removes all token state from memory.
  private clearTokens(): void {
    this.accessToken = null;
    this.accessTokenExpiresAt = null;
    if (this.refreshTimeoutHandle !== null) {
      window.clearTimeout(this.refreshTimeoutHandle);
      this.refreshTimeoutHandle = null;
    }
  }

  private isAccessTokenExpired(): boolean {
    if (this.accessTokenExpiresAt === null) {
      return true;
    }

    return Date.now() >= this.accessTokenExpiresAt - 60_000;
  }

  // Returns true once an access token is available or the refresh fails.
  private async ensureAccessToken(): Promise<boolean> {
    if (this.accessToken && !this.isAccessTokenExpired()) {
      return true;
    }

    return this.refreshAccessToken();
  }

  // Attempts to refresh tokens via the backend refresh endpoint.
  private async refreshAccessToken(emitError = true): Promise<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = (async () => {
      try {
        const response = await firstValueFrom(
          this.http.post<TokenResponse>('/api/auth/refresh', {})
        );
        this.persistTokens(response);
        this._loggedIn$.next(true);
        this._error$.next(false);
        return true;
      } catch (error) {
        if (error instanceof HttpErrorResponse) {
          this.handleAuthFailure(error, emitError);
          return false;
        }
        this.handleAuthFailure(undefined, emitError);
        return false;
      } finally {
        this.refreshInFlight = undefined;
      }
    })();

    return this.refreshInFlight;
  }

  // Derives the expiration timestamp from the issued token or payload.
  private resolveAccessTokenExpiry(response: TokenResponse): number | undefined {
    const decoded = this.decodeJwt(response.access_token);
    if (decoded?.exp && Number.isFinite(decoded.exp)) {
      return decoded.exp * 1000;
    }

    if (Number.isFinite(response.expires_in) && response.expires_in > 0) {
      return Date.now() + response.expires_in * 1000;
    }

    return undefined;
  }

  // Lightweight JWT decode to inspect expiry without verifying the token.
  private decodeJwt(token: string): { exp?: number } | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) {
        return null;
      }

      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        '='
      );
      const json = atob(padded);
      return JSON.parse(json);
    } catch (error) {
      console.warn('[AuthService] Failed to decode access token payload', error);
      return null;
    }
  }

  // Resets auth state when the backend rejects the current credentials.
  private handleAuthFailure(
    error?: HttpErrorResponse,
    emitError = true
  ): void {
    void error;
    this.clearTokens();
    if (error?.status === 401) {
      this.setHasRefreshSession(false);
    }
    if (emitError) {
      this._error$.next(true);
    }
    this.setUser(undefined, false);
    this.microsoftSession = false;
  }

  private setUser(user: User | undefined, loggedIn = true): void {
    this.currentUser = user;
    this.userResolved = true;
    this._user$.next(user);
    this._loggedIn$.next(loggedIn && !!user);
  }

  // External consumers can gate refresh attempts based on this flag.
  public canAttemptSilentRefresh(): boolean {
    return this.hasRefreshSession();
  }

  private hasRefreshSession(): boolean {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }

    try {
      return window.localStorage.getItem(HAS_REFRESH_SESSION_KEY) === '1';
    } catch {
      return false;
    }
  }

  private setHasRefreshSession(value: boolean): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      if (value) {
        window.localStorage.setItem(HAS_REFRESH_SESSION_KEY, '1');
      } else {
        window.localStorage.removeItem(HAS_REFRESH_SESSION_KEY);
      }
    } catch {
      // storage access can fail, e.g. in private mode; ignore silently
    }
  }
}
